import { Injectable, signal, inject, ApplicationRef } from '@angular/core';
import { Router, NavigationEnd, ActivatedRoute, ActivatedRouteSnapshot } from '@angular/router';
import { filter } from 'rxjs/operators';
import { BreadcrumbItem } from './breadcrumbs.types';
import { TabsComponent } from '@dxo/ng-swift-ui';
import { Subscription } from 'rxjs';

export enum TabBreadcrumbBehavior {
  /**
   * Show tab in breadcrumb only when user clicks/selects it
   */
  OnSelection = 'on-selection',
  
  /**
   * Show the current/active tab in breadcrumb immediately on page load
   */
  Immediate = 'immediate'
}

@Injectable({
  providedIn: 'root'
})
export class BreadcrumbService {
  private router = inject(Router);
  private appRef = inject(ApplicationRef);
  private _breadcrumbs = signal<BreadcrumbItem[]>([]);
  private currentTabSubscription: Subscription | null = null;
  private currentRoute: ActivatedRouteSnapshot | null = null;
  private currentTabsComponent: TabsComponent | null = null;
  private tabBehavior: TabBreadcrumbBehavior | null = null;
  
  // Public readonly signal for components to consume
  readonly breadcrumbs = this._breadcrumbs.asReadonly();

  constructor() {
    // Listen to navigation events and update breadcrumbs
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => this.updateBreadcrumbs());
    
    // Build initial breadcrumbs
    setTimeout(() => this.updateBreadcrumbs(), 0);
  }

  private updateBreadcrumbs(): void {
    // Get all route snapshots first
    const routeSnapshots = this.collectRouteSnapshots(this.router.routerState.root);
    
    // Store current route for tab listening
    this.currentRoute = routeSnapshots[routeSnapshots.length - 1];
    
    // Check if any route in the hierarchy has hideBreadcrumb flag
    const hasHideBreadcrumb = routeSnapshots.some(snapshot => snapshot.data?.hideBreadcrumb);
    
    if (hasHideBreadcrumb) {
      // Hide entire breadcrumb component
      this._breadcrumbs.set([]);
      // Clean up any existing tab subscription
      this.cleanupTabSubscription();
      return;
    }
    
    const breadcrumbs: BreadcrumbItem[] = [];
    
    // Always start with Dashboard for /benefits routes (only if not hiding breadcrumbs)
    if (this.router.url.startsWith('/benefits')) {
      breadcrumbs.push({
        text: 'Dashboard',
        path: '/benefits/dashboard',
        key: 'dashboard',
        className: 'dashboard'
      });
    }
    
    // Build breadcrumbs from route snapshots
    for (const routeSnapshot of routeSnapshots) {
      const breadcrumbText = this.resolveBreadcrumbText(routeSnapshot);
      
      if (breadcrumbText && routeSnapshot.url.length > 0) {
        const url = this.getRouteUrl(routeSnapshot);
        
        // Skip if this is the same as Dashboard
        if (url !== '/benefits/dashboard') {
          // Generate CSS class name from route path
          const className = this.generateClassName(routeSnapshot);
          
          breadcrumbs.push({
            text: breadcrumbText,
            path: url,
            key: url,
            className: className
          });
        }
      }
    }

    this._breadcrumbs.set(breadcrumbs);
    
    // Set up tab listening if enabled for this route
    this.setupTabListening();
  }

  private findLeafRoute(route: ActivatedRoute): ActivatedRoute {
    let current = route;
    while (current.firstChild) {
      current = current.firstChild;
    }
    return current;
  }

  private resolveBreadcrumbText(routeSnapshot: ActivatedRouteSnapshot): string {
    const routeData = routeSnapshot.data;
    
    // Check if breadcrumb should be skipped for this route
    if (routeData?.skipBreadcrumb || routeData?.hideBreadcrumb) {
      return '';
    }
    
    // Priority 1: Explicit breadcrumbText (highest priority - breadcrumb-specific override)
    if (routeData?.breadcrumbText) {
      return routeData.breadcrumbText;
    }
    
    // Priority 2: Explicit headerText (page header text, may differ from breadcrumb)
    if (routeData?.headerText) {
      return routeData.headerText;
    }
    
    // Priority 3: Route parameters (automatic dynamic content)
    const params = Object.keys(routeSnapshot.params);
    if (params.length > 0) {
      // Use the last parameter (most specific) for breadcrumb text
      const lastParam = params[params.length - 1];
      return routeSnapshot.params[lastParam];
    }
    
    // Priority 4: Fallback to route text property
    return routeData?.text || '';
  }

  private collectRouteSnapshots(route: ActivatedRoute): ActivatedRouteSnapshot[] {
    const snapshots: ActivatedRouteSnapshot[] = [];
    
    function collectRecursive(currentRoute: ActivatedRoute) {
      snapshots.push(currentRoute.snapshot);
      if (currentRoute.firstChild) {
        collectRecursive(currentRoute.firstChild);
      }
    }
    
    collectRecursive(route);
    return snapshots;
  }

  private getRouteUrl(routeSnapshot: ActivatedRouteSnapshot): string {
    const pathSegments: string[] = [];
    let currentSnapshot: ActivatedRouteSnapshot | null = routeSnapshot;
    
    while (currentSnapshot) {
      if (currentSnapshot.url.length > 0) {
        pathSegments.unshift(...currentSnapshot.url.map(segment => segment.path));
      }
      currentSnapshot = currentSnapshot.parent;
    }
    
    return '/' + pathSegments.join('/');
  }

  private generateClassName(routeSnapshot: ActivatedRouteSnapshot): string {
    // Get the route path (e.g., 'premium', 'billing', 'claims')
    const routePath = routeSnapshot.routeConfig?.path || '';
    
    // Convert route path to CSS class name
    // Examples: 'premium' -> 'premium', ':id' -> 'claim-detail', 'billing-statements' -> 'billing-statements'
    if (routePath.startsWith(':')) {
      // For parameter routes like ':id', use a generic name based on context
      return 'detail-page';
    }
    
    // Convert camelCase/kebab-case to CSS class format
    return routePath
      .replace(/([A-Z])/g, '-$1') // camelCase to kebab-case
      .toLowerCase()
      .replace(/[^a-z0-9-]/g, '-') // replace special chars with dashes
      .replace(/-+/g, '-') // collapse multiple dashes
      .replace(/^-|-$/g, ''); // remove leading/trailing dashes
  }

  // Public methods for manual breadcrumb management
  setBreadcrumbs(breadcrumbs: BreadcrumbItem[]): void {
    this._breadcrumbs.set([...breadcrumbs]);
  }

  clearBreadcrumbs(): void {
    this._breadcrumbs.set([]);
  }

  // ===== TAB BREADCRUMB FUNCTIONALITY =====

  /**
   * Public method for components to register their tabs component for breadcrumb updates
   * This is more reliable than trying to auto-detect components
   */
  registerTabsComponent(tabsComponent: TabsComponent, initialTab?: string): void {
    console.log('BreadcrumbService: registerTabsComponent called', { 
      hasCurrentRoute: !!this.currentRoute,
      routeData: this.currentRoute?.data,
      enableTabBreadcrumbs: this.currentRoute?.data?.enableTabBreadcrumbs,
      initialTab 
    });

    // Check if current route has enableTabBreadcrumbs
    const enableTabBreadcrumbs = this.currentRoute?.data?.enableTabBreadcrumbs;
    if (!enableTabBreadcrumbs) {
      console.log('BreadcrumbService: enableTabBreadcrumbs not enabled for this route');
      return;
    }

    // Determine behavior: boolean true = immediate, enum value = specific behavior
    this.tabBehavior = this.resolveTabBehavior(enableTabBreadcrumbs);
    this.currentTabsComponent = tabsComponent;

    console.log('BreadcrumbService: Tab behavior resolved to:', this.tabBehavior);

    // Clean up any previous subscription
    this.cleanupTabSubscription();
    
    // Handle immediate behavior - show current active tab right away
    if (this.tabBehavior === TabBreadcrumbBehavior.Immediate && initialTab) {
      console.log('BreadcrumbService: Immediately showing active tab:', initialTab);
      // Use setTimeout to avoid potential reactivity issues
      setTimeout(() => {
        this.updateBreadcrumbsWithTab(initialTab);
      }, 0);
    }
    
    console.log('BreadcrumbService: Setting up tab change subscription');
    
    // Listen to tab changes (for both OnSelection and Immediate behaviors)
    this.currentTabSubscription = tabsComponent.tabChanged.subscribe((selectedTabLabel: string) => {
      console.log('BreadcrumbService: Tab changed to:', selectedTabLabel);
      this.updateBreadcrumbsWithTab(selectedTabLabel);
    });
  }

  /**
   * Resolve the tab behavior from route data
   */
  private resolveTabBehavior(enableTabBreadcrumbs: boolean | TabBreadcrumbBehavior): TabBreadcrumbBehavior {
    if (typeof enableTabBreadcrumbs === 'boolean') {
      return enableTabBreadcrumbs ? TabBreadcrumbBehavior.Immediate : TabBreadcrumbBehavior.OnSelection;
    }
    return enableTabBreadcrumbs;
  }

  /**
   * Get the currently active tab from the tabs component
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private getCurrentActiveTab(_tabsComponent: TabsComponent): string | null {
    // For now, we'll return null and rely on user interaction
    // The TabsComponent API would need to be checked for actual active tab access
    return null;
  }

  /**
   * Sets up automatic tab breadcrumb listening if enabled for current route
   */
  private setupTabListening(): void {
    // Clean up any previous subscription
    this.cleanupTabSubscription();
    
    // Check if current route has enableTabBreadcrumbs
    if (!this.currentRoute?.data?.enableTabBreadcrumbs) {
      return;
    }
    
    // We'll wait for components to register themselves rather than auto-detecting
    // This is more reliable and explicit
  }

  /**
   * Updates breadcrumbs to include the selected tab
   */
  private updateBreadcrumbsWithTab(selectedTabLabel: string): void {
    console.log('BreadcrumbService: updateBreadcrumbsWithTab called with:', selectedTabLabel);
    
    const currentBreadcrumbs = this._breadcrumbs();
    console.log('BreadcrumbService: Current breadcrumbs:', currentBreadcrumbs);
    
    // Remove any existing tab breadcrumbs (assume last breadcrumb might be a tab)
    const baseBreadcrumbs = this.removeTabBreadcrumbs(currentBreadcrumbs);
    
    // Make the current page (last breadcrumb) non-clickable since user is on it
    const updatedBaseBreadcrumbs = baseBreadcrumbs.map((breadcrumb, index) => {
      if (index === baseBreadcrumbs.length - 1) {
        // Remove path from last breadcrumb to make it non-clickable
        return {
          ...breadcrumb,
          path: undefined,
          href: undefined
        };
      }
      return breadcrumb;
    });
    
    // Add the selected tab as a non-clickable breadcrumb
    const updatedBreadcrumbs: BreadcrumbItem[] = [
      ...updatedBaseBreadcrumbs,
      {
        text: selectedTabLabel,
        key: selectedTabLabel.toLowerCase().replace(/\s+/g, '-'),
        className: 'tab-breadcrumb'
        // No path = non-clickable
      }
    ];
    
    console.log('BreadcrumbService: Updated breadcrumbs:', updatedBreadcrumbs);
    
    this._breadcrumbs.set(updatedBreadcrumbs);
  }

  /**
   * Remove existing tab breadcrumbs from the breadcrumb array
   * This assumes tab breadcrumbs have the 'tab-breadcrumb' className
   */
  private removeTabBreadcrumbs(breadcrumbs: BreadcrumbItem[]): BreadcrumbItem[] {
    return breadcrumbs.filter(breadcrumb => breadcrumb.className !== 'tab-breadcrumb');
  }

  /**
   * Clean up tab subscription
   */
  private cleanupTabSubscription(): void {
    if (this.currentTabSubscription) {
      this.currentTabSubscription.unsubscribe();
      this.currentTabSubscription = null;
    }
  }

}
